import React, { useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';

// Common prescribing-error categories. Authoring uses these slugs in
// expectedIssues; the component shows the human label in the dropdown.
export const ISSUE_TYPES = [
  { value: 'wrong_dose', label: 'Wrong dose' },
  { value: 'wrong_frequency', label: 'Wrong frequency' },
  { value: 'wrong_route', label: 'Wrong route' },
  { value: 'interaction', label: 'Drug-drug interaction' },
  { value: 'allergy_mismatch', label: 'Allergy mismatch' },
  { value: 'contraindication', label: 'Contraindication' },
  { value: 'duplication', label: 'Duplicate therapy' },
  { value: 'missing_indication', label: 'Missing / incorrect indication' },
  { value: 'renal_adjust_needed', label: 'Renal dose adjustment needed' },
  { value: 'hepatic_adjust_needed', label: 'Hepatic dose adjustment needed' },
  { value: 'monitoring_missing', label: 'Required monitoring missing' },
  { value: 'unnecessary', label: 'Unnecessary / no longer indicated' },
  { value: 'missing_prophylaxis', label: 'Missing prophylaxis (e.g. VTE, gastric)' },
];

const labelForIssue = (slug) => ISSUE_TYPES.find((t) => t.value === slug)?.label || slug;

const normaliseDrug = (s) => String(s || '').toLowerCase().trim();

const ChartAnnotation = ({ question, prescriptions = [] }) => {
  const expectedIssues = useMemo(() => Array.isArray(question?.expectedIssues) ? question.expectedIssues : [], [question]);
  // The chart rows the student can flag - prefer the live prescription list,
  // fall back to drugs mentioned in expectedIssues if the editor preview is
  // missing the chart.
  const flaggableRows = useMemo(() => {
    if (Array.isArray(prescriptions) && prescriptions.length) {
      return prescriptions.map((rx) => ({ drug: rx.drug, dose: rx.dose, frequency: rx.frequency }));
    }
    return expectedIssues.map((iss) => ({ drug: iss.drug }));
  }, [prescriptions, expectedIssues]);

  const [flags, setFlags] = useState({}); // { drug: issueTypeSlug }
  const [submitted, setSubmitted] = useState(false);

  const setFlag = (drug, issueType) => {
    setFlags((curr) => ({ ...curr, [drug]: issueType }));
  };
  const clearFlag = (drug) => {
    setFlags((curr) => {
      const next = { ...curr };
      delete next[drug];
      return next;
    });
  };

  const submittedFlagsList = useMemo(() => Object.entries(flags)
    .filter(([, issueType]) => issueType)
    .map(([drug, issueType]) => ({ drug, issueType })), [flags]);

  // Score by precision + recall on (drug, issueType) pairs.
  const scoring = useMemo(() => {
    if (!submitted) return null;
    const expectedKey = (iss) => `${normaliseDrug(iss.drug)}::${iss.issueType}`;
    const expectedSet = new Set(expectedIssues.map(expectedKey));
    const submittedSet = new Set(submittedFlagsList.map(expectedKey));
    let truePositives = 0;
    submittedSet.forEach((k) => { if (expectedSet.has(k)) truePositives += 1; });
    const falsePositives = submittedSet.size - truePositives;
    const falseNegatives = expectedSet.size - truePositives;
    const missed = expectedIssues.filter((iss) => !submittedSet.has(expectedKey(iss)));
    const wronglyFlagged = submittedFlagsList.filter((sub) => !expectedSet.has(expectedKey(sub)));
    const precision = submittedSet.size === 0 ? 0 : truePositives / submittedSet.size;
    const recall = expectedSet.size === 0 ? 1 : truePositives / expectedSet.size;
    return {
      truePositives,
      falsePositives,
      falseNegatives,
      missed,
      wronglyFlagged,
      precision: Math.round(precision * 100),
      recall: Math.round(recall * 100),
    };
  }, [submitted, expectedIssues, submittedFlagsList]);

  const isExpectedDrug = (drug) => expectedIssues.some((iss) => normaliseDrug(iss.drug) === normaliseDrug(drug));
  const expectedIssueFor = (drug) => expectedIssues.find((iss) => normaliseDrug(iss.drug) === normaliseDrug(drug));

  return (
    <Container className="mt-3 p-3 bg-light text-dark rounded chart-annotation-question">
      <h3 className="d-flex flex-wrap align-items-center gap-2">
        Q{question.questionNumber}: {question.questionTitle}
        {scoring ? (
          <Badge bg={scoring.recall === 100 && scoring.precision === 100 ? 'success' : 'warning'} text={scoring.recall === 100 && scoring.precision === 100 ? 'light' : 'dark'}>
            {scoring.truePositives} / {expectedIssues.length} caught · {scoring.precision}% precision
          </Badge>
        ) : null}
      </h3>
      <hr />
      <p className="mb-3">{question.questionText || 'Identify any prescribing issues on the chart. Flag each problematic prescription with the appropriate issue category.'}</p>

      <div className="chart-annotation-question__grid">
        {flaggableRows.map((rx) => {
          const flagged = flags[rx.drug];
          const isExpected = isExpectedDrug(rx.drug);
          const expected = expectedIssueFor(rx.drug);
          let cellTone = '';
          if (submitted) {
            if (flagged && expected && flagged === expected.issueType) cellTone = 'chart-annotation-question__row--correct';
            else if (flagged && !isExpected) cellTone = 'chart-annotation-question__row--false-positive';
            else if (!flagged && isExpected) cellTone = 'chart-annotation-question__row--missed';
            else if (flagged && expected && flagged !== expected.issueType) cellTone = 'chart-annotation-question__row--wrong-category';
          }
          return (
            <div key={rx.drug} className={`chart-annotation-question__row ${cellTone}`}>
              <div className="chart-annotation-question__rx">
                <strong>{rx.drug}</strong>
                {rx.dose ? <span className="text-muted ms-2">{rx.dose}{rx.frequency ? ` · ${rx.frequency}` : ''}</span> : null}
              </div>
              <Form.Select
                size="sm"
                value={flagged || ''}
                disabled={submitted}
                onChange={(e) => {
                  if (!e.target.value) clearFlag(rx.drug);
                  else setFlag(rx.drug, e.target.value);
                }}
              >
                <option value="">No issue</option>
                {ISSUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Form.Select>
              {submitted && expected ? (
                <div className="small text-muted">
                  <strong>Expected:</strong> {labelForIssue(expected.issueType)}
                  {expected.explanation ? <span> — {expected.explanation}</span> : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {submitted && scoring ? (
        <div className="chart-annotation-question__summary mt-3">
          {scoring.missed.length > 0 ? (
            <div className="alert alert-warning py-2 mb-2">
              <strong>Missed:</strong> {scoring.missed.map((m) => `${m.drug} (${labelForIssue(m.issueType)})`).join('; ')}
            </div>
          ) : null}
          {scoring.wronglyFlagged.length > 0 ? (
            <div className="alert alert-info py-2 mb-0">
              <strong>Flagged but no issue / wrong category:</strong> {scoring.wronglyFlagged.map((s) => `${s.drug} (${labelForIssue(s.issueType)})`).join('; ')}
            </div>
          ) : null}
        </div>
      ) : null}

      {!submitted ? (
        <Button
          type="button"
          variant="primary"
          className="mt-3"
          onClick={() => setSubmitted(true)}
        >
          Submit annotations
        </Button>
      ) : null}
    </Container>
  );
};

export default ChartAnnotation;
